import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Badge from "@/v2-backoffice-client/components/badge"
import type { InfoGridProps } from "@/v2-backoffice-client/components/infoGrid"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import { FENERUM_PLAN_BASE_URL } from "@/v2-backoffice-client/constants"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  LICENSE_PLAN_NO_PRODUCT,
  LICENSE_PLAN_STATUS,
} from "@/v2-backoffice-client/license/plan/license.plan.constants"
import { getLicensePlanStatus } from "@/v2-backoffice-client/license/plan/license.plan.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

const FIELDS = createInfoGridFields<LicensePlanFragment>({
  fields: {
    // Status leads: deprecation recolors every decision on the panel (don't
    // upsell, work the Migration tab, offer the successor), and a license
    // that still needs setup explains its blank usage before anything else.
    DeprecatedAt: {
      label: "Status",
      accessor: (data) => {
        const status = getLicensePlanStatus(data)
        return status === LICENSE_PLAN_STATUS.Deprecated
          ? `Deprecated ${formatDateString(data.DeprecatedAt)}`
          : status
      },
      renderValue({ data }) {
        const status = getLicensePlanStatus(data)
        if (status === LICENSE_PLAN_STATUS.Live) {
          return <DotText variant="success-1">{status}</DotText>
        }
        if (status === LICENSE_PLAN_STATUS.NeedsSetup) {
          return (
            <span className="flex items-center gap-1.5">
              <DotText variant="destructive">{status}</DotText>
              <span className="text-muted-foreground">
                no product, so the import skips it
              </span>
            </span>
          )
        }
        return (
          <span className="flex items-center gap-1.5">
            <DotText variant="warning">
              Deprecated {formatDateString(data.DeprecatedAt)}
            </DotText>
            {data.SuccessorPlanName && (
              <span className="text-muted-foreground">
                → {data.SuccessorPlanName}
              </span>
            )}
          </span>
        )
      },
    },
    // The two fields we own come next: a license without a product is the
    // first thing an operator must see, because the Fenerum import derives
    // nothing from it until one is set; without seat semantics its customers
    // get no seat count.
    ProductName: {
      label: "Product",
      accessor: (data) => data.ProductName ?? LICENSE_PLAN_NO_PRODUCT,
      renderValue({ data }) {
        if (!data.ProductName) {
          return (
            <DotText variant="destructive">{LICENSE_PLAN_NO_PRODUCT}</DotText>
          )
        }
        return <Badge size="sm">{data.ProductName}</Badge>
      },
    },
    SeatSourceName: {
      label: "Seat Semantics",
      accessor: (data) => data.SeatSourceName ?? "Undeclared",
      renderValue({ data }) {
        if (!data.SeatSourceName) {
          return <DotText variant="destructive">Undeclared</DotText>
        }
        return data.SeatSourceName
      },
    },
    // The license's headline number, visible on every tab. The Revenue tab
    // holds the story behind it (rank, share, per-term split).
    Mrr: {
      label: "Monthly Run-Rate",
      accessor: (data) =>
        data.Mrr != null
          ? formatCompactAmount(data.Mrr, data.MrrCurrency ?? undefined)
          : null,
    },
    LiveSeats: {
      label: "Live Seats",
      accessor: "LiveSeats",
    },
    Code: {
      label: "Fenerum Code",
      accessor: "Code",
      defaultHidden: true,
    },
    Description: {
      label: "Description",
      accessor: "Description",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    LicenseCount: {
      label: "Licenses",
      accessor: "LicenseCount",
      defaultHidden: true,
    },
    VatType: {
      label: "VAT",
      accessor: "VatType",
      defaultHidden: true,
    },
    IsSelfServiceEnabled: {
      label: "Fenerum Self-Service",
      accessor: (data) => (data.IsSelfServiceEnabled ? "Yes" : "No"),
      defaultHidden: true,
    },
    ExternalPlanUuid: {
      label: "Fenerum Id",
      accessor: "ExternalPlanUuid",
      to: ({ data }) => `${FENERUM_PLAN_BASE_URL}/${data.ExternalPlanUuid}/`,
      valueClassName: "truncate",
      defaultHidden: true,
    },
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
  },
})

export type LicensePlanViewInfoProps = Omit<
  InfoGridProps<LicensePlanFragment>,
  "data" | "fields"
> & {
  plan: LicensePlanFragment | null | undefined
}

function LicensePlanViewInfo(props: LicensePlanViewInfoProps) {
  const { plan, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="license-plan-info"
      className={cn("license-plan-view-info", className)}
      data={plan}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicensePlanViewInfo
